import { CSSProperties } from "react";
import "./Heading.css";

interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  variant: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  children: React.ReactNode;
  ariaLabelledBy?: string;
  className?: string;
  style?: CSSProperties;
  id?: string;
}

export const Heading = ({
  variant,
  children,
  className,
  ...rest
}: HeadingProps) => {
  switch (variant) {
  case "h1":
    return (
      <h1
        className={`heading heading-h1 ${className ? className : ""}`}
        {...rest}
      >
        {children}
      </h1>
    );

  case "h2":
    return (
      <h2
        className={`heading heading-h2 ${className ? className : ""}`}
        {...rest}
      >
        {children}
      </h2>
    );

  case "h3":
    return (
      <h3
        className={`heading heading-h3 ${className ? className : ""}`}
        {...rest}
      >
        {children}
      </h3>
    );

  case "h4":
    return (
      <h4
        className={`heading heading-h4 ${className ? className : ""}`}
        {...rest}
      >
        {children}
      </h4>
    );

  case "h5":
    return (
      <h5
        className={`heading heading-h5 ${className ? className : ""}`}
        {...rest}
      >
        {children}
      </h5>
    );

  case "h6":
    return (
      <h6
        className={`heading heading-h6 ${className ? className : ""}`}
        {...rest}
      >
        {children}
      </h6>
    );
  }
};

/**
 * A flexible heading component that renders semantic HTML heading elements (h1-h6).
 * Provides consistent styling and behavior across different heading levels while
 * maintaining proper document structure and accessibility.
 *
 * @component
 * @example
 * // Basic usage with different heading levels
 * <Heading variant="h1">Main Page Title</Heading>
 * <Heading variant="h2">Section Title</Heading>
 * <Heading variant="h3">Subsection Title</Heading>
 *
 * @example
 * // With custom styling and props
 * <Heading
 *   variant="h2"
 *   className="custom-heading"
 *   id="section-title"
 *   style={{ color: 'blue' }}
 * >
 *   Custom Styled Heading
 * </Heading>
 *
 * @example
 * // With accessibility attributes
 * <Heading
 *   variant="h1"
 *   role="banner"
 *   ariaLabelledBy="site-description"
 * >
 *   Welcome to Our Site
 * </Heading>
 */
